/**
 * Upload metadata modal — collects name, optional category, and optional
 * description for a file the user just dropped or picked. Returns a
 * Promise that resolves to the metadata, or `null` if the user cancelled.
 *
 * Replaces the previous chain of two `window.prompt()` calls so the upload
 * flow stays inside the app's visual language.
 */

const COMMON_CATEGORIES = [
  'Health', 'Planning', 'Demographics', 'Infrastructure',
  'Environment', 'Transportation', 'Education', 'Agriculture',
  'Energy', 'Economy',
];

export function openUploadMetaModal(file) {
  return new Promise((resolve) => {
    document.querySelector('#uploadMetaModal')?.remove();

    const stem = file.name.replace(/\.[^.]+$/, '');
    const sizeMb = (file.size / (1024 * 1024)).toFixed(2);

    document.body.insertAdjacentHTML('beforeend', `
      <div class="modal-bg show" id="uploadMetaModal">
        <div class="modal" style="width:440px">
          <h3>Upload layer</h3>
          <div style="display:flex;gap:10px;align-items:center;padding:10px 12px;background:var(--bg3);border:1px solid var(--border);border-radius:6px;margin:10px 0 16px">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" stroke-width="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/></svg>
            <div style="min-width:0;flex:1">
              <div style="font-size:12.5px;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${escape(file.name)}</div>
              <div style="font-size:10.5px;color:var(--text3);font-family:var(--mono)">${sizeMb} MB</div>
            </div>
          </div>

          <form id="upMetaForm" style="display:flex;flex-direction:column;gap:12px">
            <label style="display:block;font-size:11px;color:var(--text2)">
              Layer name <span style="color:var(--red)">*</span>
              <input name="name" required value="${escape(stem)}" autocomplete="off"
                     style="width:100%;margin-top:4px;padding:8px 10px;border-radius:6px;border:1px solid var(--border);background:var(--bg3);color:var(--text);font-size:13px">
            </label>

            <label style="display:block;font-size:11px;color:var(--text2)">
              Category <span style="color:var(--text3);font-style:italic">(optional)</span>
              <input name="category" list="upMetaCategories" autocomplete="off"
                     placeholder="e.g. ${COMMON_CATEGORIES[0]}, ${COMMON_CATEGORIES[2]}…"
                     style="width:100%;margin-top:4px;padding:8px 10px;border-radius:6px;border:1px solid var(--border);background:var(--bg3);color:var(--text);font-size:13px">
              <datalist id="upMetaCategories">
                ${COMMON_CATEGORIES.map(c => `<option value="${escape(c)}">`).join('')}
              </datalist>
            </label>

            <label style="display:block;font-size:11px;color:var(--text2)">
              Description <span style="color:var(--text3);font-style:italic">(optional)</span>
              <textarea name="description" rows="2"
                        placeholder="What's in this dataset? Source, vintage, license…"
                        style="width:100%;margin-top:4px;padding:8px 10px;border-radius:6px;border:1px solid var(--border);background:var(--bg3);color:var(--text);font-size:12.5px;font-family:var(--font);resize:vertical;line-height:1.4"></textarea>
            </label>

            <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:6px">
              <button type="button" class="btn" data-up-meta="cancel">Cancel</button>
              <button type="submit" class="btn btn-primary">Upload</button>
            </div>
          </form>
        </div>
      </div>
    `);

    const root  = document.getElementById('uploadMetaModal');
    const form  = root.querySelector('#upMetaForm');
    const input = form.querySelector('input[name=name]');
    setTimeout(() => { input.focus(); input.select(); }, 30);

    let resolved = false;
    const finish = (val) => {
      if (resolved) return;
      resolved = true;
      document.removeEventListener('keydown', onKey);
      root.remove();
      resolve(val);
    };

    const onKey = (e) => { if (e.key === 'Escape') finish(null); };
    document.addEventListener('keydown', onKey);

    root.addEventListener('click', (e) => {
      // Click on the backdrop (outside the modal) → cancel.
      if (e.target === root) finish(null);
      const cancel = e.target.closest('[data-up-meta="cancel"]');
      if (cancel) finish(null);
    });

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const fd = new FormData(form);
      const name = String(fd.get('name') || '').trim();
      if (!name) { input.focus(); return; }
      finish({
        name,
        category:    String(fd.get('category')    || '').trim() || undefined,
        description: String(fd.get('description') || '').trim() || undefined,
      });
    });
  });
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[c]));
}
